const _ = require("lodash");

// Given an object with x, y, w, h keys, convert it to an [x, y, w, h] array.
const rectToArray = function(obj) {
	if (!obj) {
		return obj;
	} else if (obj.hasOwnProperty(0) && obj.slice) {
		return obj.slice(0, 4);
	}
	return [obj.x, obj.y, obj.w, obj.h];
};

// Given an [x, y, w, h] array, convert to an object with keys x, y, w, h.
const rectToObj = function(arr) {
	if (!arr) {
		return arr;
	}
	if (arr.hasOwnProperty("x") || arr.hasOwnProperty("w")) {
		return {x: arr.x, y: arr.y, w: arr.w, h: arr.h};
	}
	return {x: arr[0], y: arr[1], w: arr[2], h: arr[3]};
};

//Given two rects in object form, check if they are overlapping
const isRectOverlap = function(rect1, rect2) {
	rect1 = rectToObj(rect1);
	rect2 = rectToObj(rect2);
	return !(
		rect1.x > rect2.x + rect2.w ||
		rect2.x > rect1.x + rect1.w ||
		rect1.y > rect2.y + rect2.h ||
		rect2.y > rect1.y + rect1.h
	);
};

const pointsEqual = function([x1, y1], [x2, y2]) {
	return x1 === x2 && y1 === y2;
};

/*
 * Determines if 2 lines are equal.
 *
 * Assumption: lines are points of 2 finite 'number' types in form [[x1, y1], [x2, y2]].
 *             (Though it also works in the case of point indices like [pt1, pt2])
 *
 * @param {Array} firstLine   The first line to compare.
 * @param {Array} secondLine  The second line to compare.
 */
const linesEqual = (firstLine, secondLine) => {
	const sorted1 = _.sortBy(firstLine);
	const sorted2 = _.sortBy(secondLine);

	return _.isEqual(sorted1, sorted2);
};

//Given two rects in object form, check if their areas (not just edges) overlap
const isRectOverlapArea = function(rect1, rect2) {
	rect1 = rectToObj(rect1);
	rect2 = rectToObj(rect2);
	return !(
		rect1.x >= rect2.x + rect2.w ||
		rect2.x >= rect1.x + rect1.w ||
		rect1.y >= rect2.y + rect2.h ||
		rect2.y >= rect1.y + rect1.h
	);
};

//Given two rects in object form, check if the first is in the second.
const isRectInside = function(rect1, rect2) {
	rect1 = rectToObj(rect1);
	rect2 = rectToObj(rect2);
	return (
		rect1.x >= rect2.x &&
		rect1.x + rect1.w <= rect2.x + rect2.w &&
		rect1.y >= rect2.y &&
		rect1.y + rect1.h <= rect2.y + rect2.h
	);
};

//Given two polyominoes specified by x, y (top left) and shape, a matrix of 0s and 1s where 1s are included squares,
//return true if they overlap and false otherwise
const isPolyominoOverlap = function(pol1, pol2) {
	for (let i = 0; i < pol1.shape[0].length; i++) {
		for (let j = 0; j < pol1.shape.length; j++) {
			if (pol1.shape[j][i]) {
				const x = i + pol1.x - pol2.x;
				const y = j + pol1.y - pol2.y;
				if (pol2.shape[y] && pol2.shape[y][x]) {
					return true;
				}
			}
		}
	}
	return false;
};

// Given a rect and a list of rects, find the index of the closest rectangle that collides
const closestRect = function(rect, rectList) {
	let maxAreaIndex = null;
	let maxArea = 0;
	for (let i = 0; i < rectList.length; i += 1) {
		const checkRect = rectList[i];
		const xOverlap =
			Math.min(checkRect.x + checkRect.w, rect.x + rect.w) -
			Math.max(checkRect.x, rect.x);
		const yOverlap =
			Math.min(checkRect.y + checkRect.h, rect.y + rect.h) -
			Math.max(checkRect.y, rect.y);
		if (xOverlap * yOverlap > maxArea) {
			maxArea = xOverlap * yOverlap;
			maxAreaIndex = i;
		}
	}
	return maxAreaIndex;
};

// Given a rect and a list of rects, find the index of the closest rectangle in the X and Y direction
// It finds the ones with closest Y first, then the one with closest X
const closestRectXY = function(rect, rectList) {
	let closestIndex = null;
	let minDy = 100000;
	let minDx = 100000;
	let checkRect;
	let i;
	for (i = 0; i < rectList.length; i++) {
		checkRect = rectList[i];
		if (checkRect && Math.abs(rect.y - checkRect.y) <= minDy) {
			minDy = Math.abs(rect.y - checkRect.y);
			closestIndex = i;
		}
	}
	for (i = 0; i < rectList.length; i++) {
		checkRect = rectList[i];
		if (checkRect && Math.abs(rect.y - checkRect.y) === minDy) {
			if (Math.abs(rect.x - checkRect.x) <= minDx) {
				minDx = Math.abs(rect.x - checkRect.x);
				closestIndex = i;
			}
		}
	}
	return closestIndex;
};

// Given a list of 2D points, return the smallest rectangle containing all of them.
const getBoundingRect = function(pList) {
	const x1 = Math.min(...pList.map(p => p[0]));
	const y1 = Math.min(...pList.map(p => p[1]));
	const x2 = Math.max(...pList.map(p => p[0]));
	const y2 = Math.max(...pList.map(p => p[1]));
	return [x1, y1, x2 - x1, y2 - y1];
};

// Given two rects (array or object), return the largest rectangle contained in
// both in [x, y, w, h] form. If either rect is falsy or no intersection, return null.
const intersectRects = function(r1, r2) {
	if (!r1 || !r2) {
		return null;
	}
	r1 = rectToArray(r1);
	r2 = rectToArray(r2);
	const x1 = Math.max(r1[0], r2[0]);
	const y1 = Math.max(r1[1], r2[1]);
	const x2 = Math.min(r1[0] + r1[2], r2[0] + r2[2]);
	const y2 = Math.min(r1[1] + r1[3], r2[1] + r2[3]);
	if (x2 < x1 || y2 < y1) {
		return null;
	}
	return [x1, y1, x2 - x1, y2 - y1];
};

// Given two rects (array or object), return the smallest rectangle containing
// both in [x, y, w, h] form. If either rect is false, return the other one.
const unionRects = function(r1, r2) {
	if (!r1 || !r2) {
		return r1 || r2;
	}
	r1 = rectToArray(r1);
	r2 = rectToArray(r2);
	const x1 = Math.min(r1[0], r2[0]);
	const y1 = Math.min(r1[1], r2[1]);
	const x2 = Math.max(r1[0] + r1[2], r2[0] + r2[2]);
	const y2 = Math.max(r1[1] + r1[3], r2[1] + r2[3]);
	return [x1, y1, x2 - x1, y2 - y1];
};

// Given an array of rectangles, return the smallest rectangle containing all
// of them. Basically unionRects with more than two arguments.
const unionRectsList = function(rList) {
	let filteredList = rList && rList.filter ? rList.filter(Boolean) : null;
	if (!filteredList || !filteredList.length) {
		return null;
	}
	filteredList = filteredList.map(r => rectToArray(r));
	const x1 = Math.min(...filteredList.map(r => r[0]));
	const y1 = Math.min(...filteredList.map(r => r[1]));
	const x2 = Math.max(...filteredList.map(r => r[0] + r[2]));
	const y2 = Math.max(...filteredList.map(r => r[1] + r[3]));
	return [x1, y1, x2 - x1, y2 - y1];
};

// Return the smallest rectangle with integer coordinates containing the given
// one in [x,y,w,h] form.
//If the innerRect option is set, this instead returns the largest rectangle with
//integer coordinates contained within the given one in [x,w,y,h] form.
const makeRectInt = function(rect, opts = {}) {
	rect = rectToArray(rect);
	const coordT = opts.innerRect ? Math.ceil : Math.floor;
	const dimT = opts.innerRect ? Math.floor : Math.ceil;
	const x1 = coordT(rect[0]);
	const y1 = coordT(rect[1]);
	const x2 = dimT(rect[0] + rect[2]);
	const y2 = dimT(rect[1] + rect[3]);
	return [x1, y1, x2 - x1, y2 - y1];
};

// Given a rect (array or object) and an amount to pad it by, return the padded
// rectangle in [x, y, w, h] form. Positive values enlarge the rectangle.
// Can accept 1 2 3 or 4 parameters in the same manner as CSS.
const padRect = function(rect, pt, pr, pb, pl) {
	rect = rectToArray(rect);
	pr = pr !== undefined ? pr : pt;
	pb = pb !== undefined ? pb : pt;
	pl = pl !== undefined ? pl : pr;
	return [rect[0] - pl, rect[1] - pt, rect[2] + pl + pr, rect[3] + pt + pb];
};

// padRect, except that each padding amount is scaled by the dimension of the
// padding.
const padRectProportional = function(rect, pt, pr, pb, pl) {
	rect = rectToArray(rect);
	pr = pr !== undefined ? pr : pt;
	pb = pb !== undefined ? pb : pt;
	pl = pl !== undefined ? pl : pr;
	pt *= rect[3];
	pr *= rect[2];
	pb *= rect[3];
	pl *= rect[2];
	return [rect[0] - pl, rect[1] - pt, rect[2] + pl + pr, rect[3] + pt + pb];
};

//Takes in two rect objects and interpolates
const interpolateRects = function(r1, r2, t) {
	return {
		x: r1.x * (1 - t) + r2.x * t,
		y: r1.y * (1 - t) + r2.y * t,
		w: r1.w * (1 - t) + r2.w * t,
		h: r1.h * (1 - t) + r2.h * t,
	};
};

// Returns a point [x, y] fit inside the provided rectangle. Optionally provide
// a margin to extend the rectangle by (or to contract, if negative).
const clampPointInRect = function(x, y, rect, margin = 0) {
	rect = rectToArray(rect);
	return [
		Math.max(rect[0] - margin, Math.min(rect[0] + rect[2] + margin, x)),
		Math.max(rect[1] - margin, Math.min(rect[1] + rect[3] + margin, y)),
	];
};

// Center and fit the rIn inside rOut, which are rectangles in either [x,y,w,h]
// or {x,y,w,h} form. Returns an object with translate, scale, and fitRect keys.
// translate and scale are the amount to translate and scale by to make rIn fit,
// where the translation is applied before the scaling. fitRect is an [x,y,w,h]
// array giving rIn after the transformations.
// Options that can be set:
// - scaleOff: Always use a scaling factor of 1.
// - scaleDownOnly: Never use a scaling factor above 1.
// - distort: use different scaling factors for x and y
const fitInRect = function(rIn, rOut, options) {
	options = options || {};
	rIn = rectToArray(rIn);
	rOut = rectToArray(rOut);

	let translateX = 0;
	let translateY = 0;
	let scalingFactor = 1;

	if (!options.scaleOff) {
		scalingFactor = Math.min(rOut[2] / rIn[2], rOut[3] / rIn[3]);
	}
	if (options.scaleDownOnly) {
		scalingFactor = Math.min(1, scalingFactor);
	}

	let xScale = scalingFactor;
	let yScale = scalingFactor;

	if (options.distort) {
		xScale = rOut[2] / rIn[2];
		yScale = rOut[3] / rIn[3];
		scalingFactor = [xScale, yScale];
	}

	// Amount to move the top left corner by.
	translateX += rOut[0] - rIn[0] * xScale;
	translateY += rOut[1] - rIn[1] * yScale;

	// Amount of x, y in the container rect not used by the content.
	const missingX = rOut[2] - rIn[2] * xScale;
	const missingY = rOut[3] - rIn[3] * yScale;
	translateX += missingX / 2;
	translateY += missingY / 2;

	return {
		translate: [translateX, translateY],
		scale: [xScale, yScale],
		fitRect: [
			rOut[0] + missingX / 2,
			rOut[1] + missingY / 2,
			rIn[2] * xScale,
			rIn[3] * yScale,
		],
	};
};

const centerInRect = function(rOuter, rInner) {
	const widthDiff = rOuter.w - rInner.w;
	const halfWidthDiff = widthDiff / 2.0;

	const heightDiff = rOuter.h - rInner.h;
	const halfHeightDiff = heightDiff / 2;

	return {
		x: rOuter.x + halfWidthDiff,
		y: rOuter.y + halfHeightDiff,
		w: rInner.w,
		h: rInner.h,
	};
};

// Given a rectangle and a point, return whether the rectangle contains the point.
const isInRect = function(rect, x, y) {
	rect = rectToArray(rect);
	return (
		!!rect &&
		x >= rect[0] &&
		x <= rect[0] + rect[2] &&
		y >= rect[1] &&
		y <= rect[1] + rect[3]
	);
};

//Given a polygon specified by an array of points: [[x1,y1],[x2,y2],etc.],
//return whether or not the polygon contains the point.
//This is an implementation of the even odd rule.
const isInPoly = function(poly, x, y) {
	if (poly.length <= 2) {
		return false;
	}
	const boundingRect = getBoundingRect(poly);
	if (!isInRect(boundingRect, x, y)) {
		return false;
	}
	const maxPolyX = poly.map(a => a[0]).reduce((a, b) => Math.max(a, b));
	const ray = [
		[x, y],
		[maxPolyX + 1, y],
	];
	let intersections = 0;
	for (let i = 0; i < poly.length; i++) {
		const p1 = poly[i];
		const p2 = poly[(i + 1) % poly.length];
		if (
			doSegmentsIntersect(ray, [p1, p2]) &&
			!(y === p1[1] && p2[1] >= y) &&
			!(y === p2[1] && p1[1] >= y)
		) {
			intersections++;
		}
	}
	return intersections % 2 === 1;
};

// Get the signed area of a list of points. Useful for determining a winding
// number if you know the polygon is not self-intersecting.
const getSignedPolyArea = function(pList) {
	const n = pList.length;
	let area = 0;
	pList.forEach((p1, i) => {
		const p2 = pList[(i + 1) % n];
		area += p1[0] * p2[1] - p1[1] * p2[0];
	});
	return area;
};

//Based on code found at http://stackoverflow.com/questions/9043805/test-if-two-lines-intersect-javascript-function
const doSegmentsIntersect = function(s1, s2) {
	if (s1 && s2 && s1[0] && s1[1] && s2[0] && s2[1]) {
		const det =
			(s1[1][0] - s1[0][0]) * (s2[1][1] - s2[0][1]) -
			(s2[1][0] - s2[0][0]) * (s1[1][1] - s1[0][1]);
		if (det === 0) {
			return (
				(s1[0][0] === s2[0][0] &&
					s1[0][1] === s2[0][1] &&
					s1[1][0] === s2[1][0] &&
					s1[1][1] === s2[1][1]) ||
				(s1[1][0] === s2[0][0] &&
					s1[1][1] === s2[0][1] &&
					s1[0][0] === s2[1][0] &&
					s1[0][1] === s2[1][1])
			);
		} else {
			const t1 =
				((s2[1][1] - s2[0][1]) * (s2[1][0] - s1[0][0]) +
					(s2[0][0] - s2[1][0]) * (s2[1][1] - s1[0][1])) /
				det;
			const t2 =
				((s1[0][1] - s1[1][1]) * (s2[1][0] - s1[0][0]) +
					(s1[1][0] - s1[0][0]) * (s2[1][1] - s1[0][1])) /
				det;
			return 0 <= t1 && t1 <= 1 && 0 <= t2 && t2 <= 1;
		}
	} else {
		return false;
	}
};

// Returns boolean of whether point is on the line segment formed by points
// Returns false if point equals one of the segment endpoints.
const isPointOnSegment = function(
	point,
	startPoint,
	endPoint,
	marginOfError = 0.01
) {
	const [distance] = pointSegmentDistance(point[0], point[1], [
		startPoint,
		endPoint,
	]);
	return distance < marginOfError;
};

// Finds any intersection point of two 2D polygons. Returns null if none.
const polygonIntersection = function(p1, p2) {
	const wrappedSegInt = (v1, v2, v3, v4) => {
		const i = segmentsIntersection(
			v1[0],
			v1[1],
			v2[0],
			v2[1],
			v3[0],
			v3[1],
			v4[0],
			v4[1]
		);
		return i ? [i.x, i.y] : null;
	};
	if (p1.length === 2 && p2.length === 2) {
		if (doSegmentsIntersect(p1, p2)) {
			return wrappedSegInt(p1[0], p1[1], p2[0], p2[1]);
		}
		return null;
	}
	const r1 = getBoundingRect(p1);
	const r2 = getBoundingRect(p2);
	if (!intersectRects(r1, r2)) {
		return null;
	}
	for (let i = 0; i < p1.length; i += 1) {
		if (isInPoly(p2, p1[i][0], p1[i][1])) {
			return p1[i];
		}
	}
	for (let i = 0; i < p2.length; i += 1) {
		if (isInPoly(p1, p2[i][0], p2[i][1])) {
			return p2[i];
		}
	}
	const segs1 = p1.map((p, i) => [p, p1[(i + 1) % p1.length]]);
	const segs2 = p2.map((p, i) => [p, p2[(i + 1) % p2.length]]);
	for (let i = 0; i < segs1.length; i += 1) {
		for (let j = 0; j < segs2.length; j += 1) {
			if (doSegmentsIntersect(segs1[i], segs2[j])) {
				return wrappedSegInt(
					segs1[i][0],
					segs1[i][1],
					segs2[j][0],
					segs2[j][1]
				);
			}
		}
	}
	return null;
};

//Based on http://paulbourke.net/geometry/pointlineplane/
//p1-p2, p3-p4 two segments
const segmentsIntersection = function(x1, y1, x2, y2, x3, y3, x4, y4) {
	const den = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);
	if (!den) {
		return false;
	}
	const s = ((x4 - x3) * (y1 - y4) - (y4 - y3) * (x1 - x4)) / den;
	return {
		x: x1 + s * (x2 - x1),
		y: y1 + s * (y2 - y1),
	};
};

// Returns whether the line from (x1,y1) to (x2,y2) intersects the rect.
const lineIntersectsRect = function(x1, y1, x2, y2, rect) {
	rect = rectToArray(rect);
	// Degenerate case with vertical line.
	if (x1 === x2) {
		return rect[0] <= x1 && rect[0] + rect[3] >= x1;
	}
	// Look for intersections with either vertical segment of rect.
	// yLeft is the y-coordinate of the point on the line whose x-coordinate is
	// the left edge of the rect.
	const yLeft = y1 + ((y2 - y1) * (rect[0] - x1)) / (x2 - x1);
	if (yLeft >= rect[1] && yLeft <= rect[1] + rect[3]) {
		return true;
	}
	const yRight = y1 + ((y2 - y1) * (rect[0] + rect[2] - x1)) / (x2 - x1);
	if (yRight >= rect[1] && yRight <= rect[1] + rect[3]) {
		return true;
	}
	return false;
};

// Returns whether a segment intersects any part of a rectangular area
const segmentIntersectsRectArea = function(x1, y1, x2, y2, rect) {
	rect = rectToArray(rect);
	const p1 = [rect[0], rect[1]];
	const p2 = [rect[0] + rect[2], rect[1]];
	const p3 = [rect[0] + rect[2], rect[1] + rect[3]];
	const p4 = [rect[0], rect[1] + rect[3]];
	return (
		!!doSegmentsIntersect(x1, y1, x2, y2, p1[0], p1[1], p2[0], p2[1]) ||
		!!doSegmentsIntersect(x1, y1, x2, y2, p1[0], p1[1], p4[0], p4[1]) ||
		!!doSegmentsIntersect(x1, y1, x2, y2, p2[0], p2[1], p3[0], p3[1]) ||
		!!doSegmentsIntersect(x1, y1, x2, y2, p3[0], p3[1], p4[0], p4[1]) ||
		isInRect(rect, x1, y1) ||
		isInRect(rect, x2, y2)
	);
};

// Returns whether the ray from (x1,y1) to (x2,y2) intersects the rect.
const rayIntersectsRect = function(x1, y1, x2, y2, rect) {
	rect = rectToArray(rect);
	// If the line intersects the rectangle, then we can determine if the ray does
	// by checking the obvious conditions on each axis separately.
	if (!lineIntersectsRect(x1, y1, x2, y2, rect)) {
		return false;
	} else if (x2 <= x1 && rect[0] > x1) {
		return false;
	} else if (x2 >= x1 && rect[0] + rect[2] < x1) {
		return false;
	} else if (y2 <= y1 && rect[1] > y1) {
		return false;
	} else if (y2 >= y1 && rect[1] + rect[3] < y1) {
		return false;
	}
	return true;
};

//Given a point inside of a rectangle (ix, iy) and a point outside or on that rectangle (ox, oy),
//Find where the ray from (ix, iy) to (ox, oy) intersects the rectangle
const intersectRayThroughRect = function(ix, iy, ox, oy, rect) {
	rect = rectToArray(rect);
	let px, py;
	if (ox <= rect[0]) {
		//Trying the left side of the rectangle
		px = rect[0];
		py = iy + ((oy - iy) * (px - ix)) / (ox - ix);
		if (py >= rect[1] && py <= rect[1] + rect[3]) {
			return [px, py];
		}
	}
	if (oy <= rect[1]) {
		//Trying the top side of the rectangle
		py = rect[1];
		px = ix + ((ox - ix) * (py - iy)) / (oy - iy);
		if (px >= rect[0] && px <= rect[0] + rect[2]) {
			return [px, py];
		}
	}
	if (ox >= rect[0] + rect[2]) {
		//Trying the right side of the rectangle
		px = rect[0] + rect[2];
		py = iy + ((oy - iy) * (px - ix)) / (ox - ix);
		if (py >= rect[1] && py <= rect[1] + rect[3]) {
			return [px, py];
		}
	}
	if (oy >= rect[1] + rect[3]) {
		//Trying the bottom side of the rectangle
		py = rect[1] + rect[3];
		px = ix + ((ox - ix) * (py - iy)) / (oy - iy);
		if (px >= rect[0] && px <= rect[0] + rect[2]) {
			return [px, py];
		}
	}
	return null;
};

// Given a Canvas transformation matrix m, return its inverse.
const invertTransform = function(m) {
	const det = m[0] * m[3] - m[1] * m[2];
	if (!det) {
		return null;
	}
	return [
		m[3] / det,
		-m[1] / det,
		-m[2] / det,
		m[0] / det,
		(m[2] * m[5] - m[3] * m[4]) / det,
		(m[1] * m[4] - m[0] * m[5]) / det,
	];
};

// Given a Canvas transformation matrix m and a point (x, y), return the
// transformed position of the point as a 2 element array.
const transformPoint = function(m, x, y) {
	return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
};

// Given a Canvas transformation matrix m and a rectangle in object or array
// form, compute the transformed rectangle, then return the smallest rectangle
// containing it as an [x, y, w, h] array.
const transformRect = function(m, rect) {
	rect = rectToArray(rect);
	const p0 = transformPoint(m, rect[0], rect[1]);
	const p1 = transformPoint(m, rect[0] + rect[2], rect[1]);
	const p2 = transformPoint(m, rect[0] + rect[2], rect[1] + rect[3]);
	const p3 = transformPoint(m, rect[0], rect[1] + rect[3]);

	const x1 = Math.min(p0[0], p1[0], p2[0], p3[0]);
	const y1 = Math.min(p0[1], p1[1], p2[1], p3[1]);
	const x2 = Math.max(p0[0], p1[0], p2[0], p3[0]);
	const y2 = Math.max(p0[1], p1[1], p2[1], p3[1]);
	return [x1, y1, x2 - x1, y2 - y1];
};

//Given two points, return their distance
const pointPointDistance = function(x1, y1, x2, y2) {
	return Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
};

//Given a point and the two endpoints of a line segment as an array [[x1,y1],[x2,y2]],
//return the distance from the point to that segment and the closest point on that segment in the form [distance, pointX, pointY]
//Based on code found at http://stackoverflow.com/questions/849211/shortest-distance-between-a-point-and-a-line-segment
const pointSegmentDistance = function(x, y, segmentEndpoints) {
	const a = x - segmentEndpoints[0][0];
	const b = y - segmentEndpoints[0][1];
	const c = segmentEndpoints[1][0] - segmentEndpoints[0][0];
	const d = segmentEndpoints[1][1] - segmentEndpoints[0][1];
	const dotProduct = a * c + b * d;
	const length = pointPointDistance(0, 0, c, d);
	const mult = length !== 0 ? dotProduct / (length * length) : -1;

	let closestX, closestY;
	if (mult < 0) {
		closestX = segmentEndpoints[0][0];
		closestY = segmentEndpoints[0][1];
	} else if (mult > 1) {
		closestX = segmentEndpoints[1][0];
		closestY = segmentEndpoints[1][1];
	} else {
		closestX = segmentEndpoints[0][0] + mult * c;
		closestY = segmentEndpoints[0][1] + mult * d;
	}

	return [pointPointDistance(x, y, closestX, closestY), closestX, closestY];
};

// Compute the distance from the point to the closest point on the rectangle. 0
// if the point is inside the rectangle.
const pointRectDistance = function(px, py, rect) {
	rect = rectToObj(rect);
	if (!rect) {
		return null;
	}
	// Distances from point to rect along x or y axis only.
	const xDist = Math.max(rect.x - px, px - rect.x - rect.w, 0);
	const yDist = Math.max(rect.y - py, py - rect.y - rect.h, 0);
	return Math.sqrt(xDist * xDist + yDist * yDist);
};

// Given two rectangles, return a distance based on look at corresponding vertices.
const rectRectDistance = function(r1, r2) {
	r1 = rectToObj(r1);
	r2 = rectToObj(r2);
	if (!r1 || !r2) {
		return null;
	}
	return (
		(pointPointDistance(r1.x, r1.y, r2.x, r2.y) +
			pointPointDistance(r1.x + r1.w, r1.y + r1.h, r2.x + r2.w, r2.y + r2.h)) /
		2
	);
};

const computeAngle = function(cx, cy, p1x, p1y, p2x, p2y) {
	const ux = p1x - cx;
	const uy = p1y - cy;
	const vx = p2x - cx;
	const vy = p2y - cy;
	const dot = ux * vx + uy * vy;
	const det = ux * vy - uy * vx;
	return Math.atan2(det, dot);
};

const rotatePointAroundPoint = function(angle, cx, cy, px, py) {
	const x = cx + (px - cx) * Math.cos(angle) - (py - cy) * Math.sin(angle);
	const y = cy + (px - cx) * Math.sin(angle) + (py - cy) * Math.cos(angle);
	return [x, y];
};

// If a ray is drawn from a point at a direction of baseAngle (radians from
// x-axis), return the smallest positive amount the angle would have to be
// altered for the ray to intersect the rectangle.
const rectAngleFromPoint = function(px, py, baseAngle, rect) {
	rect = rectToObj(rect);
	if (!rect) {
		return null;
	}
	// Ray direction.
	const dx = Math.cos(baseAngle);
	const dy = Math.sin(baseAngle);
	// Check if already intersecting.
	if (rayIntersectsRect(px, py, px + dx, py + dy, rect)) {
		return 0;
	}
	// Function computing angle difference from r1's center to a point.
	const computeDiff = (x, y) =>
		Math.abs(computeAngle(px, py, px + dx, py + dy, x, y));
	// Angles to each corner of rect
	const toUL = computeDiff(rect.x, rect.y);
	const toUR = computeDiff(rect.x + rect.w, rect.y);
	const toDL = computeDiff(rect.x, rect.y + rect.h);
	const toDR = computeDiff(rect.x + rect.w, rect.y + rect.h);
	return Math.min(toUL, toUR, toDL, toDR);
};

// Given two rectangles, return an array of two booleans. The first is whether
// the rects overlap when projected onto the x-axis. The second is whether the
// rects overlap when projected onto the y-axis. Does not include boundaries
// of rectangles. If the optional tolerance is provided, rectangles must
// overlap by that much when projected.
const rectAxisOverlap = function(r1, r2, tolerance) {
	r1 = rectToObj(r1);
	r2 = rectToObj(r2);
	if (!r1 || !r2) {
		return [false, false];
	}
	tolerance = +tolerance || 0;
	return [
		r1.x + tolerance < r2.x + r2.w && r2.x + tolerance < r1.x + r1.w,
		r1.y + tolerance < r2.y + r2.h && r2.y + tolerance < r1.y + r1.h,
	];
};

const isPointInCircle = function(px, py, cx, cy, r) {
	return r >= Math.sqrt((px - cx) * (px - cx) + (py - cy) * (py - cy));
};

// rx: horizontal radius
// ry: vertical radius
const isPointInEllipse = function(px, py, cx, cy, rx, ry) {
	return (py - cy) ** 2 / ry ** 2 + (px - cx) ** 2 / rx ** 2 <= 1;
};

/**
 *Takes in the centers and the radii of two circles and determines whether or not
 *they have an intersection and returns the intersection points if they do. Note that
 *in the case of one intersection, the function returns the same coordinate pair twice.
 **/
const circleIntersections = function(x1, y1, r1, x2, y2, r2) {
	const d = pointPointDistance(x1, y1, x2, y2); //distance between centers

	if (d > r1 + r2) {
		return null;
	} else if (d < Math.abs(r1 - r2)) {
		return null;
	}

	const t = (r1 * r1 - r2 * r2 + d * d) / (2 * d * d);
	const xi = x1 + (x2 - x1) * t; //(xi, yi) is instersection of radical axis of two circles and the line connecting the centers of the two circles
	const yi = y1 + (y2 - y1) * t;
	//console.log(xi, yi, t);
	const a = t * d; //distance from (xi, yi) to center of circle 1

	const h = Math.sqrt(r1 * r1 - a * a); //distance between (xi, yi) and each of the circles' intersections

	const x3 = xi - (h / d) * (y2 - y1);
	const y3 = yi + (h / d) * (x2 - x1);
	const x4 = xi + (h / d) * (y2 - y1);
	const y4 = yi - (h / d) * (x2 - x1);

	return [
		[x3, y3],
		[x4, y4],
	];
};

const getRectAtCenter = function(center, rect) {
	return {
		x: center[0] - rect.w / 2,
		y: center[1] - rect.h / 2,
		w: rect.w,
		h: rect.h,
	};
};

// Convert to degrees from radians
const toDeg = function(rad) {
	return Math.round((rad / Math.PI) * 180);
};

// Simplify to smallest equivalent radian value
const simplifyRad = function(rad) {
	return (rad + 2 * Math.PI) % (2 * Math.PI);
};

module.exports = {
	rectToArray,
	rectToObj,
	isRectOverlap,
	isRectOverlapArea,
	isRectInside,
	isPolyominoOverlap,
	closestRect,
	closestRectXY,
	pointsEqual,
	linesEqual,
	getBoundingRect,
	intersectRects,
	unionRects,
	unionRectsList,
	makeRectInt,
	padRect,
	padRectProportional,
	interpolateRects,
	centerInRect,
	isInRect,
	getSignedPolyArea,
	isInPoly,
	lineIntersectsRect,
	segmentIntersectsRectArea,
	rayIntersectsRect,
	intersectRayThroughRect,
	clampPointInRect,
	fitInRect,
	invertTransform,
	transformPoint,
	transformRect,
	pointPointDistance,
	pointSegmentDistance,
	pointRectDistance,
	rectRectDistance,
	computeAngle,
	rotatePointAroundPoint,
	rectAngleFromPoint,
	rectAxisOverlap,
	doSegmentsIntersect,
	isPointOnSegment,
	isPointInCircle,
	isPointInEllipse,
	segmentsIntersection,
	circleIntersections,
	polygonIntersection,
	getRectAtCenter,
	toDeg,
	simplifyRad,
};
